/**
 * PWA Visibility Revalidation Hook
 *
 * Addresses the "no repaint until focus" issue on Android PWA where:
 * - Refresh completes in background but UI doesn't update until user switches apps
 *
 * On visibility/focus return:
 * - Force React Query to revalidate critical queries
 * - Trigger a safe re-render check
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isInstalledPWA } from '@/utils/pwa';
import { logger } from '@/lib/logger';

const REVALIDATION_DEBOUNCE_MS = 500;

export const usePWAVisibilityRevalidation = () => {
  const queryClient = useQueryClient();
  const lastRevalidation = useRef<number>(0);

  useEffect(() => {
    // Only active for installed PWA
    if (!isInstalledPWA()) return;

    const revalidate = () => {
      const now = Date.now();
      if (now - lastRevalidation.current < REVALIDATION_DEBOUNCE_MS) {
        return;
      }
      lastRevalidation.current = now;

      if (import.meta.env.DEV) {
        logger.log('[PWAVisibility] Revalidating queries on focus/visibility');
      }

      // Invalidate critical dashboard/home queries to ensure fresh data
      // This uses a broad invalidation that will refresh active queries
      queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey[0];
          // Invalidate common dashboard data queries
          return typeof key === 'string' && (
            key.includes('symptoms') ||
            key.includes('medications') ||
            key.includes('appointments') ||
            key.includes('fatigue') ||
            key.includes('moods') ||
            key.includes('streak') ||
            key.includes('profile') ||
            key.includes('home')
          );
        },
        refetchType: 'active', // Only refetch currently active queries
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        revalidate();
      }
    };

    const handleFocus = () => {
      revalidate();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      // bfcache restoration
      if (event.persisted) {
        revalidate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [queryClient]);
};
